import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, ChevronRight, Gem, Handshake, Search as SearchIcon, TrendingUp, Scale } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, EyeToggle, Money, Section, Spinner } from "@/components/kit";
import ActivityList from "@/components/ActivityList";
import { ItemRow } from "@/components/Rows";
import { firstName } from "@/lib/format";

const CARDS = [
  { id: "stock", label: "Stocks", icon: TrendingUp },
  { id: "commodity", label: "Commodities", icon: Gem },
  { id: "business", label: "Business Partnerships", icon: Handshake },
];

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

function PositionCard({ position, hidden }) {
  const s = position.summary;
  const tone = s.direction === "owed_to_you" ? "text-emerald-700" : s.direction === "you_owe" ? "text-[#B34832]" : "text-[#1A202C]";
  return (
    <Link to="/ledger" data-testid="home-settlement-position" className="hd-card flex items-center gap-4 hover:border-[#C8553D]/40 transition-colors">
      <div className="w-12 h-12 rounded-2xl bg-[#F9ECE8] text-[#C8553D] flex items-center justify-center"><Scale size={22} /></div>
      <div className="flex-1">
        <div className="text-sm text-[#718096]">{s.text}</div>
        {s.direction !== "settled" ? (
          <Money value={s.amount} hidden={hidden} className={`text-xl font-bold ${tone}`} testid="home-position-amount" />
        ) : (
          <div className="text-xl font-bold text-emerald-700" data-testid="home-position-amount">All settled</div>
        )}
      </div>
      <ChevronRight size={18} className="text-[#A0AEC0]" />
    </Link>
  );
}

export default function Home() {
  const { gid, person } = useAuth();
  const [d, setD] = useState(null);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    api.get(`/groups/${gid}/dashboard`).then((r) => setD(r.data));
  }, [gid]);

  if (!d) return <Spinner />;
  const needs = d.pending.needs_my_action;
  const edits = d.pending.edit_requested;
  const waiting = d.pending.awaiting_others.length;
  const activeCount = CARDS.reduce((a, c) => a + d.cards[c.id].active, 0);
  return (
    <div className="pb-4">
      <header className="flex items-center justify-between pt-5">
        <div className="flex items-center gap-2 text-[#C8553D]">
          <span className="w-8 h-8 rounded-lg bg-[#C8553D] text-white flex items-center justify-center font-extrabold">%</span>
          <span className="text-xl font-extrabold tracking-tight" data-testid="home-brand">HisseDari</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/search" data-testid="home-search-button" className="w-10 h-10 rounded-full bg-white border border-[#E2D9CF] flex items-center justify-center"><SearchIcon size={18} /></Link>
          <Link to="/profile" data-testid="home-avatar"><Avatar name={person.full_name} id={person.id} size={40} /></Link>
        </div>
      </header>

      <div className="mt-6 fade-up">
        <div className="text-2xl font-bold tracking-tight">{greeting()}, {firstName(person.full_name)}</div>
        <div className="mt-2 hd-chip bg-white border border-[#E2D9CF] text-[#4A5568]" data-testid="home-current-group">Group · {d.group.name}</div>
      </div>

      <div className="mt-5 rounded-3xl bg-[#C8553D] text-white p-5 relative overflow-hidden fade-up" style={{ animationDelay: "60ms" }}>
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10" />
        <div className="flex items-start justify-between relative">
          <div>
            <div className="text-sm text-white/80">Total jointly invested (at cost)</div>
            <Money value={d.total_invested} hidden={hidden} className="block text-3xl font-bold mt-1 tracking-tight" testid="home-total-invested" />
            <div className="text-sm text-white/80 mt-1.5">{activeCount} active holdings</div>
          </div>
          <EyeToggle hidden={hidden} onToggle={() => setHidden((h) => !h)} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2.5 mt-3">
        {CARDS.map((c, i) => (
          <Link key={c.id} to={`/holdings?tab=${c.id}`} data-testid={`home-card-${c.id}`} className="hd-card p-3.5 hover:border-[#C8553D]/40 transition-colors fade-up" style={{ animationDelay: `${100 + i * 40}ms` }}>
            <div className="w-9 h-9 rounded-xl bg-[#F9ECE8] text-[#C8553D] flex items-center justify-center"><c.icon size={18} /></div>
            <div className="text-xs font-semibold mt-3 leading-tight min-h-[30px]">{c.label}</div>
            <div className="text-2xl font-bold mt-1" data-testid={`home-card-${c.id}-count`}>{d.cards[c.id].active}</div>
            <div className="text-[11px] text-[#718096]">active</div>
            <Money value={d.cards[c.id].invested} hidden={hidden} className="block text-xs font-semibold mt-1 text-[#4A5568] truncate" />
          </Link>
        ))}
      </div>

      <Section title="Pending Confirmations" action={<Link to="/pending" data-testid="home-pending-see-all" className="text-sm font-semibold text-[#C8553D]">See all</Link>}>
        {needs.length === 0 && edits.length === 0 ? (
          <div className="hd-card text-sm text-[#718096] flex items-center gap-3" data-testid="home-pending-empty">
            <Bell size={18} className="text-[#C8553D]/60" /> Nothing awaiting your confirmation{waiting ? ` · ${waiting} awaiting the other side` : ""}
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="home-pending-list">
            {needs.slice(0, 3).map((i) => (
              <div key={i.id} className="hd-card py-2 border-l-4 border-l-amber-400">
                <div className="text-xs font-semibold text-amber-700 pt-1">{firstName(i.submitted_by_name)} has added a {i.kind} requiring your confirmation.</div>
                <ItemRow item={i} />
              </div>
            ))}
            {edits.slice(0, 2).map((i) => (
              <div key={i.id} className="hd-card py-2 border-l-4 border-l-blue-400">
                <div className="text-xs font-semibold text-blue-700 pt-1">An edit was requested on your {i.kind}.</div>
                <ItemRow item={i} />
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Settlement Position">
        <PositionCard position={d.position} hidden={hidden} />
      </Section>

      <Section title="Recent Activity" action={<Link to="/activity" data-testid="home-activity-see-all" className="text-sm font-semibold text-[#C8553D]">See all</Link>}>
        <ActivityList items={d.recent_activity} testid="home-activity" />
      </Section>
    </div>
  );
}
